"use client";

import type { ReactNode } from "react";
import { useId, useState } from "react";
import { SidebarDisclosure } from "@/app/components/ui/SidebarDisclosure";
import {
  AlignLeft, Box, ChevronDown, Droplet, Layers, Maximize2,
  Move, PlayCircle, Scan, SlidersHorizontal, Sparkles, Square, Type,
} from "lucide-react";

export type BuilderInspectorSettingsSectionProps = {
  children: ReactNode;
  label: string;
  legacyDataAttributes?: Record<string, string | undefined>;
  sectionKey: string;
};

const sectionIcons = {
  content: AlignLeft,
  style: Droplet,
  colors: Droplet,
  typography: Type,
  layout: Move,
  size: Maximize2,
  spacing: Scan,
  border: Square,
  effects: Sparkles,
  motion: PlayCircle,
  layer: Layers,
  "footer-layout": Box,
};

/** Adjacent sections form one settings list with a shared outer border. */
export function BuilderInspectorSettingsSection({
  children,
  label,
  legacyDataAttributes,
  sectionKey,
}: BuilderInspectorSettingsSectionProps) {
  const [open, setOpen] = useState(false);
  const contentId = useId();
  const Icon = sectionIcons[sectionKey as keyof typeof sectionIcons] ?? SlidersHorizontal;

  return (
    <section
      className="builder-inspector-section"
      {...legacyDataAttributes}
      data-builder-inspector-settings-section={sectionKey}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls={contentId}
        className="builder-inspector-section-trigger"
        onClick={() => setOpen((current) => !current)}
      >
        <Icon aria-hidden="true" className="h-[18px] w-[18px] shrink-0" strokeWidth={1.65} />
        <span className="min-w-0 flex-1 text-[13px] font-medium leading-5">
          {label}
        </span>
        <ChevronDown
          aria-hidden="true"
          className={[
            "h-4 w-4 shrink-0 text-text-secondary transition-transform duration-300 ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-none",
            open ? "rotate-180" : "",
          ].join(" ")}
        />
      </button>
      <SidebarDisclosure id={contentId} open={open} unmountOnExit>
        <div className="builder-inspector-section-content space-y-2">
          {children}
        </div>
      </SidebarDisclosure>
    </section>
  );
}
